import { useId, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3, Table2 } from 'lucide-react';
import { cn } from '@tailoring-crm/ui';

/**
 * A chart that is a table underneath, and can be shown as one on demand.
 *
 * No charting library. Two reasons, and the second is the real one:
 *
 *  - a bar chart of bounded categories is a flexbox row of divs with percentage heights, and a
 *    charting library is 90KB over a connection that is often a phone on 4G in a market;
 *  - at 360px a conventional chart is unreadable and its tooltips are untappable, so the honest
 *    fallback is a table — and once the table has to exist and be correct, the chart is the
 *    decoration on top of it rather than the thing itself.
 *
 * So the data is rendered as a `<table>` in the DOM at all times for screen readers and for the
 * "show as table" toggle, and the bars are `aria-hidden` presentation drawn from the same rows.
 * There is no state in which what is drawn and what is listed can disagree.
 */

export type ChartView = 'chart' | 'table';

/**
 * The "chart / table" switch every chart carries. Exported so the Home dashboard's charts offer
 * the same two buttons, worded and behaving the same way, as the reports do.
 */
export function ChartViewToggle({
  view,
  onChange,
}: {
  view: ChartView;
  onChange: (view: ChartView) => void;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <div role="group" aria-label={t('reports.viewAs')} className="flex gap-1">
      {(['chart', 'table'] as const).map((one) => {
        const Icon = one === 'chart' ? BarChart3 : Table2;
        return (
          <button
            key={one}
            type="button"
            aria-pressed={view === one}
            onClick={() => onChange(one)}
            className={cn(
              'flex items-center gap-1 rounded-md border px-2 py-1 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              view === one
                ? 'border-transparent bg-primary text-primary-foreground'
                : 'border-border hover:bg-accent',
            )}
          >
            <Icon aria-hidden="true" className="size-3.5" />
            {t(`reports.view.${one}`)}
          </button>
        );
      })}
    </div>
  );
}

export interface ChartPoint {
  /** The x-axis label. Kept short: on a phone only every few are drawn. */
  label: string;
  value: number;
  /** What the value reads as — a formatted rupee figure, or a count. */
  display: string;
}

export function ReportChart({
  title,
  points,
  valueHeading,
  labelHeading,
  initialView = 'chart',
}: {
  title: string;
  points: readonly ChartPoint[];
  valueHeading: string;
  labelHeading: string;
  initialView?: 'chart' | 'table';
}): ReactElement {
  const { t } = useTranslation();
  const [view, setView] = useState<ChartView>(initialView);
  const tableId = useId();

  const max = points.reduce((highest, point) => Math.max(highest, point.value), 0);
  const empty = points.every((point) => point.value === 0);

  return (
    <section className="flex flex-col gap-2" aria-label={title}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">{title}</h2>
        <ChartViewToggle view={view} onChange={setView} />
      </div>

      {empty ? <p className="text-sm text-muted-foreground">{t('reports.noData')}</p> : null}

      {view === 'chart' && !empty ? (
        // Horizontally scrollable rather than squeezed: forty days of bars at 360px would be
        // sub-pixel columns, and a chart nobody can read is worse than one they have to swipe.
        <div className="overflow-x-auto">
          <div
            aria-hidden="true"
            className="flex h-40 min-w-full items-end gap-1"
            style={{ minWidth: `${Math.max(points.length * 14, 100)}px` }}
          >
            {points.map((point, index) => (
              <div
                key={`${point.label}-${String(index)}`}
                className="flex min-w-3 flex-1 flex-col justify-end"
                title={`${point.label}: ${point.display}`}
              >
                <div
                  className="rounded-t bg-primary/80"
                  style={{ height: `${max === 0 ? 0 : Math.max(2, (point.value / max) * 100)}%` }}
                />
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <table className={cn('w-full text-sm', view === 'chart' && !empty && 'sr-only')} id={tableId}>
        <caption className="sr-only">{title}</caption>
        <thead>
          <tr className="text-left text-xs text-muted-foreground">
            <th scope="col" className="py-1 font-medium">
              {labelHeading}
            </th>
            <th scope="col" className="py-1 text-right font-medium">
              {valueHeading}
            </th>
          </tr>
        </thead>
        <tbody>
          {points.map((point, index) => (
            <tr key={`${point.label}-${String(index)}`} className="border-t border-border">
              <th scope="row" className="py-1.5 text-left font-normal">
                {point.label}
              </th>
              <td className="py-1.5 text-right tabular-nums">{point.display}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
